import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../../features/auth/slice';
import { Upload, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import PublicLayout from '../../components/public/PublicLayout';
import { PUBLIC_BASE } from './config';
import UserAvatar from '../../components/UserAvatar';

const BOARD_PATHS = {
  PNotice: 'notice',
  PFree: 'free',
};

const BOARD_LABELS = {
  PNotice: '공지사항',
  PFree: '자유게시판',
};

function PublicBoardWritePage({ boardType = 'PNotice' }) {
  const navigate = useNavigate();
  const { me } = useSelector(authSelector.selectAuthState);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const boardPath = BOARD_PATHS[boardType];
  const writerName = me?.nickName || me?.name;

  // Auth guard
  useEffect(() => {
    if (!me) {
      navigate(`${PUBLIC_BASE}/login`);
      return;
    }
    if (boardType === 'PNotice' && !(me.userType === 'client' && me.adminYn === 'Y')) {
      alert('관리자만 작성할 수 있습니다.');
      navigate(`${PUBLIC_BASE}/community/${boardPath}`);
    }
  }, [me, boardType, navigate, boardPath]);

  useEffect(() => {
    axios
      .get('/api/public/board/category/load', {
        params: { boardType },
        withCredentials: true,
      })
      .then((response) => {
        setCategories(response.data);
        if (response.data.length > 0) {
          setSelectedCategory(response.data[0].id.toString());
        }
      });
  }, [boardType]);

  const handleSubmit = () => {
    if (!title.trim()) {
      alert('제목을 입력해 주세요.');
      return;
    }
    if (!content.trim()) {
      alert('내용을 입력해 주세요.');
      return;
    }

    const formData = new FormData();
    if (file) formData.append('file', file);
    formData.append('writer', writerName);
    formData.append('title', title);
    formData.append('description', content);
    formData.append('categoryId', selectedCategory);
    formData.append('isNotice', 0);
    formData.append('secretYn', 'N');

    setLoading(true);
    axios
      .post(`/api/public/board?boardType=${boardType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      })
      .then((response) => {
        navigate(`${PUBLIC_BASE}/community/${boardPath}/view/${response.data.newArticle.id}`);
      })
      .catch((err) => {
        alert('등록 중 오류가 발생했습니다.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  if (!me) return null;

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
          <div className="px-6 py-4 bg-white/[0.03] border-b border-white/[0.08] text-slate-200 text-base font-semibold">
            {BOARD_LABELS[boardType]} 글쓰기
          </div>

          <div className="p-6 space-y-5">
            {/* 작성자 */}
            <div className="flex items-center gap-3">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-20 shrink-0">
                작성자
              </label>
              <div className="flex items-center gap-2">
                <UserAvatar
                  name={writerName}
                  profileImage={me?.profileImage}
                  userType={me?.userType}
                  adminYn={me?.adminYn}
                  size={24}
                />
                <span className="text-sm text-slate-200">{writerName}</span>
              </div>
            </div>

            {/* 카테고리 */}
            {categories.length > 1 && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  카테고리
                </label>
                <select
                  className="w-full px-3 py-2 rounded-xl border border-white/[0.08] text-sm outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent bg-[#1a2b42] text-slate-200 transition"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id.toString()}>
                      {category.category}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* 제목 */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">제목</label>
              <input
                className="w-full py-2 px-3 rounded-xl border border-white/[0.08] text-sm outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent bg-[#1a2b42] text-slate-200 transition"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="제목을 입력하세요"
              />
            </div>

            {/* 내용 */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">내용</label>
              <textarea
                className="w-full h-64 p-3 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent resize-y placeholder-slate-500 transition"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="내용을 입력하세요"
              />
            </div>

            {/* 파일 첨부 */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                파일 첨부
              </label>
              <label className="flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-white/[0.12] bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer transition-colors">
                <Upload size={18} className="text-slate-500" />
                <span className="text-sm text-slate-400">
                  {file ? file.name : '파일을 선택하세요'}
                </span>
                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={() => navigate(`${PUBLIC_BASE}/community/${boardPath}`)}
            className="flex items-center gap-1.5 px-4 py-2 text-slate-400 hover:text-white text-sm font-medium transition-colors">
            <ArrowLeft size={16} />
            목록
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-5 py-2 rounded-xl text-white text-sm font-semibold transition-colors ${
              loading
                ? 'bg-slate-600 cursor-not-allowed'
                : 'bg-sky-500 hover:bg-sky-600'
            }`}>
            {loading ? '등록 중...' : '등록'}
          </button>
        </div>
      </div>
    </PublicLayout>
  );
}

export default PublicBoardWritePage;
